import React, { useCallback, useState } from 'react';
import './App.css';
import DataViewer from './components/DataViewer';
import NeraViewer from './components/NeraViewer';
import PicselliaDatasetManager from './components/PicselliaDatasetManager';
import HsiViewer from './components/HsiViewer';
import TpaViewer from './components/TpaViewer';
import QueryTab from './components/QueryTab';
import EtlDashboard from './components/EtlDashboard';
import DatasetRegistryTab from './components/DatasetRegistryTab';
import JobsTab from './components/JobsTab';
import InferenceTab from './components/InferenceTab';
import ModelRegistryTab from './components/ModelRegistryTab';
import ConversionsTab from './components/ConversionsTab';
import Login from './components/Login';
import { useAuth } from './auth/AuthContext';
import { ToastProvider } from './components/ToastNotifications';
import { LifecycleFocus } from './lifecycle';
import { tokens } from './components/ui/controls';

type Tab = 'dataviewer' | 'nera-viewer' | 'dataset-manager' | 'hsi-viewer' | 'tpa-viewer' | 'query' | 'etl' | 'datasets' | 'jobs' | 'inference' | 'models' | 'cloud-conversions' | 'embedded-conversions';

type NavSection = { label?: string; items: Array<{ id: Tab; label: string }> };

const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      { id: 'dataviewer', label: 'PSDN Viewer' },
      { id: 'nera-viewer', label: 'NERA Viewer' },
      { id: 'query', label: 'Query' },
      { id: 'etl', label: 'ETL' },
    ],
  },
  {
    label: 'Model Lifecycle',
    items: [
      { id: 'datasets', label: 'Datasets' },
      { id: 'jobs', label: 'Jobs' },
      { id: 'models', label: 'Model Registry' },
      { id: 'inference', label: 'Inference' },
      { id: 'cloud-conversions', label: 'Cloud conversions' },
      { id: 'embedded-conversions', label: 'Embedded conversions' },
    ],
  },
  {
    items: [
      { id: 'dataset-manager', label: 'Picsellia Dataset Manager' },
      { id: 'hsi-viewer', label: 'HSI Viewer' },
      { id: 'tpa-viewer', label: 'TPA Viewer' },
    ],
  },
];

const NAV_ITEMS = NAV_SECTIONS.flatMap((section) => section.items);

function navButtonStyle(selected: boolean, nested: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    textAlign: 'left',
    padding: nested ? '0.75rem 1.25rem 0.75rem 2.75rem' : '0.75rem 1.25rem',
    fontSize: '0.95rem',
    fontWeight: selected ? 'bold' : 'normal',
    background: selected ? tokens.primarySoft : 'transparent',
    border: 'none',
    borderLeft: `3px solid ${selected ? tokens.primary : 'transparent'}`,
    cursor: 'pointer',
    color: selected ? tokens.primary : tokens.text,
  };
}

export type NeraFocusTarget = {
  assessmentId: number;
  date: string; // YYYY-MM-DD
};

function AppContent() {
  const { isAuthenticated, authRequired, userEmail, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('dataviewer');
  const [navOpen, setNavOpen] = useState(false);
  const [neraFocus, setNeraFocus] = useState<NeraFocusTarget | null>(null);
  const [focus, setFocus] = useState<LifecycleFocus | null>(null);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const clearFocus = useCallback(() => setFocus(null), []);

  const openNeraAssessment = (target: NeraFocusTarget) => {
    setNeraFocus(target);
    setActiveTab('nera-viewer');
  };

  const activeLabel = NAV_ITEMS.find((item) => item.id === activeTab)?.label ?? 'Dataviewer';

  if (authRequired && !isAuthenticated) {
    return <Login />;
  }

  return (
    <div className="App" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <header
        style={{
          padding: '0.75rem 1rem',
          background: '#f8f9fa',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.75rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
          <button
            type="button"
            aria-label={navOpen ? 'Collapse navigation' : 'Expand navigation'}
            aria-expanded={navOpen}
            aria-controls="app-nav"
            onClick={() => setNavOpen((open) => !open)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 4,
              width: 40,
              height: 40,
              padding: '8px 9px',
              border: '1px solid #dee2e6',
              borderRadius: 4,
              background: navOpen ? '#e7f1ff' : '#fff',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <span style={{ display: 'block', width: '100%', height: 2, background: '#333', borderRadius: 1 }} />
            <span style={{ display: 'block', width: '100%', height: 2, background: '#333', borderRadius: 1 }} />
            <span style={{ display: 'block', width: '100%', height: 2, background: '#333', borderRadius: 1 }} />
          </button>
          <h1 style={{ margin: 0, fontSize: '1.35rem', whiteSpace: 'nowrap' }}>Dataviewer</h1>
          <span style={{ color: '#666', fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {activeLabel}
          </span>
        </div>
        {authRequired && userEmail && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
            <span style={{ color: '#666', fontSize: '0.9rem' }}>{userEmail}</span>
            <button
              type="button"
              onClick={signOut}
              style={{
                padding: '0.4rem 0.8rem',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </header>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {navOpen && (
          <nav
            id="app-nav"
            style={{
              width: 240,
              flexShrink: 0,
              background: '#fff',
              borderRight: '1px solid #dee2e6',
              padding: '0.5rem 0',
              overflowY: 'auto',
            }}
          >
            {NAV_SECTIONS.map((section) => {
              const sectionActive = section.items.some((item) => item.id === activeTab);
              const expanded = !section.label || openSection === section.label || sectionActive;
              const items = section.items.map((item) => {
                const selected = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActiveTab(item.id)}
                    style={navButtonStyle(selected, Boolean(section.label))}
                  >
                    {item.label}
                  </button>
                );
              });
              const label = section.label;
              if (!label) {
                return <div key={section.items[0].id}>{items}</div>;
              }
              return (
                <div
                  key={label}
                  onMouseEnter={() => setOpenSection(label)}
                  onMouseLeave={() => setOpenSection((current) => (current === label ? null : current))}
                  onFocus={() => setOpenSection(label)}
                  onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                      setOpenSection((current) => (current === label ? null : current));
                    }
                  }}
                >
                  <button
                    type="button"
                    aria-expanded={expanded}
                    style={{
                      ...navButtonStyle(false, false),
                      ...(sectionActive ? { fontWeight: 'bold', color: tokens.primary } : {}),
                    }}
                  >
                    <span>{label}</span>
                    <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
                  </button>
                  {expanded ? items : null}
                </div>
              );
            })}
          </nav>
        )}

        <main style={{ flex: 1, padding: '2rem', overflow: 'auto' }}>
          {activeTab === 'nera-viewer' && (
            <NeraViewer
              focus={neraFocus}
              onFocusConsumed={() => setNeraFocus(null)}
            />
          )}
          {activeTab === 'hsi-viewer' && (
            <HsiViewer onOpenNeraAssessment={openNeraAssessment} />
          )}
          {activeTab === 'tpa-viewer' && <TpaViewer />}
          {activeTab === 'dataviewer' && <DataViewer />}
          {activeTab === 'query' && <QueryTab />}
          {activeTab === 'etl' && <EtlDashboard />}
          {activeTab === 'datasets' && (
            <DatasetRegistryTab
              onTrain={(datasetName, datasetVersion) => {
                setFocus({ tab: 'jobs', mode: 'train', datasetName, datasetVersion });
                setActiveTab('jobs');
              }}
              onInfer={(datasetName, datasetVersion) => {
                setFocus({ tab: 'inference', datasetName, datasetVersion });
                setActiveTab('inference');
              }}
            />
          )}
          {activeTab === 'jobs' && (
            <JobsTab
              focus={focus}
              onFocusConsumed={clearFocus}
              onInspect={(modelName, version) => {
                setFocus({ tab: 'models', modelName, version, inspect: true });
                setActiveTab('models');
              }}
            />
          )}
          {activeTab === 'inference' && (
            <InferenceTab focus={focus} onFocusConsumed={clearFocus} />
          )}
          {activeTab === 'models' && (
            <ModelRegistryTab
              focus={focus}
              onFocusConsumed={clearFocus}
              onTest={(species, modelType, version) => {
                setFocus({ tab: 'jobs', mode: 'test', species, modelType, version });
                setActiveTab('jobs');
              }}
              onInfer={(modelName, version) => {
                setFocus({ tab: 'inference', modelName, version });
                setActiveTab('inference');
              }}
            />
          )}
          {activeTab === 'cloud-conversions' && <ConversionsTab target="cloud" />}
          {activeTab === 'embedded-conversions' && <ConversionsTab target="embedded" />}
          {activeTab === 'dataset-manager' && <PicselliaDatasetManager />}
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}

export default App;
